import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { PageTemplate } from 'components/PageTemplate';
import { Subroutes } from 'routes/Subroutes';
import { TenantContextProvider, useTenantContext } from 'contexts/TenantContext/TenantContext';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { JobExecutionContextProvider, useJobExecutionContext } from 'contexts/JobExecutionContext';
import { JobRegionProvider } from 'contexts/JobRegionContext';
import { TenantRouteProps, TenantRoutesProps } from './Tabs/TenantRoutes';
import { useAppRouteContext } from '../../contexts/AppRouteContext';
import { TenantLockStatus } from 'components/TenantLockStatus';
import { makeStyles, createStyles } from '@bb-ui/react-library/dist/components/styles';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { useAuthorization } from 'hooks/useAuthorization';
import LearnSiteContextProvider, {
  useLearnSiteContext,
} from 'contexts/LearnSiteContext/LearnSiteContext';

const useStyles = makeStyles((theme) =>
  createStyles({
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    url: {
      marginTop: '2px',
      fontSize: theme.typography.fontSizeSmall,
    },
  }),
);

const isLearnSite = (tenantType: string) =>
  tenantType === 'LearnInstance' || tenantType === 'Learn';

// Create a custom component for the title that includes the TenantLockStatus component
// only for LearnInstance and if the user has the necessary permissions. For Learn sites
// it also shows the site's URL below the name.
const CustomTitleWithLockStatus: React.FC<{ name: string; tenantType: string }> = ({
  name,
  tenantType,
}) => {
  const classes = useStyles();
  const { hasPermission } = useAuthorization();
  const { site } = useLearnSiteContext();
  const canUpdateTenant = hasPermission('tenant', 'update');
  const learnSite = isLearnSite(tenantType);
  const title = (
    <Typography variant="display2" component="h2">
      {name}
    </Typography>
  );
  return (
    <div>
      {learnSite && canUpdateTenant ? (
        <TenantLockStatus>{title}</TenantLockStatus>
      ) : (
        <div className={classes.titleContainer}>{title}</div>
      )}
      {learnSite && site?.Url && (
        <Typography className={classes.url} color="textSecondary">
          {site.Url}
        </Typography>
      )}
    </div>
  );
};

interface TenantPageContentProps {
  routes: TenantRouteProps[];
}

const TenantPageContent: React.FC<TenantPageContentProps> = ({ routes }) => {
  const { t } = useTranslation();
  const { tenant, loading, error } = useTenantContext();
  const { learnInstance } = useJobExecutionContext();
  const { currentRoute } = useAppRouteContext();
  const currentTenantRoute = currentRoute as TenantRouteProps | undefined;
  const loaded = React.useRef(false);

  if (!loaded.current && !loading) {
    loaded.current = true;
  }

  const tenantType = tenant?.tenantType || 'Not Set';
  const tenantTitle = tenant && tenant.name ? tenant.name : t('tenant.notFoundTitle') || '';
  const extras = { learnInstance };

  const enabledRoutes = () =>
    routes.filter(
      (r) =>
        (!r.tenantTypes || r.tenantTypes.includes(tenantType)) &&
        (!r.condition || !tenant || r.condition(tenant, extras)),
    );

  const disabledTabIds = () =>
    routes
      .filter(
        (r) =>
          (r.tenantTypes && !r.tenantTypes.includes(tenantType)) ||
          (r.condition && tenant && !r.condition(tenant, extras)),
      )
      .map((r) => r.path);

  return (
    <>
      {!loaded.current && loading && (
        <LoadingIndicator theme="light" variant="fill-window" data-testid="loading-tenant" />
      )}
      {loaded.current && (
        <LearnSiteContextProvider>
          <PageTemplate
            breadcrumbProps={tenant && { replaceText: { 'tenantProfile.pageTitle': tenant.name! } }}
            showTabs
            groupedTabs
            title={
              tenant ? (
                <CustomTitleWithLockStatus name={tenantTitle} tenantType={tenantType} />
              ) : (
                tenantTitle
              )
            }
            errorMessage={
              error && {
                title: t('tenant.doesNotExist'),
                message: error.message,
              }
            }
            headerControl={
              currentTenantRoute?.headerActionComponent ? (
                <currentTenantRoute.headerActionComponent />
              ) : null
            }
            disabledTabIds={disabledTabIds()}
            tabProps={{ wrapped: true }}
          >
            <Subroutes routes={enabledRoutes()} />
          </PageTemplate>
        </LearnSiteContextProvider>
      )}
    </>
  );
};

export type TenantProps = TenantRoutesProps;

export const Tenant: React.FC<TenantProps> = ({ routes }) => {
  const { tenantId } = useParams<{ tenantId: string }>();

  return (
    <TenantContextProvider tenantId={tenantId!}>
      <JobRegionProvider>
        <JobExecutionContextProvider>
          <TenantPageContent routes={routes} />
        </JobExecutionContextProvider>
      </JobRegionProvider>
    </TenantContextProvider>
  );
};
